import { map, mergeMap, retry, shareReplay } from 'rxjs/operators';
// Utils / Constants
import { STATIC_MENU_URL_MAP } from './constants';
import { STAFF_ONLY_FEATURE } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
// Types / Interfaces
import { StaticFileUrlService } from '../static-file-url.service';
// Services
import { AjaxService } from '../core/ajax.service';
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';
import * as i0 from "@angular/core";
import * as i1 from "../core/ajax.service";
import * as i2 from "../static-file-url.service";
import * as i3 from "EntitlementsApp/ts/features/feature-needed/feature.service";
/**
 * Static Menu Service
 *
 * A service that fetches menu data stored in .json files (because they are not expected to change).
 */
export class StaticMenuService {
    constructor(ajaxService, staticFileUrlService, featureService) {
        this.ajaxService = ajaxService;
        this.staticFileUrlService = staticFileUrlService;
        this.featureService = featureService;
        this.cachedMenuMap = {};
    }
    /**
     * Given the menu type and group of menu items, get the absolute URL to that file and retrieve the json data.
     * The json data describes the categories in each menu, and any sublist items under those categories.
     *
     * @param menuType The type of menu data that is fetched for i.e. metric menu.
     * @param submenuType A specific group of items under menuType i.e. popular metrics.
     */
    getStaticMenu(menuType, submenuType) {
        const staticJsonUrl = STATIC_MENU_URL_MAP[menuType][submenuType];
        const cachedMenu$ = this.cachedMenuMap[staticJsonUrl];
        if (cachedMenu$) {
            return cachedMenu$;
        }
        const menu$ = this.staticFileUrlService
            .getAbsoluteURL(staticJsonUrl)
            .pipe(mergeMap((cachedUrl) => {
            return this.ajaxService.getDataObserveBody(cachedUrl, undefined, {
                'Cache-Control': 'max-age=31536000', // Cache for 1 year
            });
        }), retry(3), shareReplay(1))
            .pipe(map((value) => {
            switch (menuType) {
                case 'metricMenu':
                    return this.filterCalcMenuCategories(value);
                case 'infoFieldMenu':
                    return this.filterInfoFieldMenuCategories(value);
                default: // if we are retrieving label maps, there is nothing to filter
                    return value;
            }
        }));
        this.cachedMenuMap[staticJsonUrl] = menu$;
        return menu$;
    }
    // Filter info field menu categories
    filterInfoFieldMenuCategories(categories) {
        const filteredCategories = categories.map((c) => ({ ...c, list_items: this.filterMenuItems(c.list_items) }));
        const nonEmptyCategories = filteredCategories.filter((c) => c.list_items.length > 0);
        if (!this.featureService.userHasFeatures([STAFF_ONLY_FEATURE])) {
            // Filter out categories labeled as "(Default)" for non staff users.
            // This is temporary, will be removed once default categories are made available to non staff
            return nonEmptyCategories.filter((c) => !c.name.includes('(Default)'));
        }
        return nonEmptyCategories;
    }
    // Filter calc field menu categories
    filterCalcMenuCategories(categories) {
        const filteredCategories = categories.map((c) => ({ ...c, sections: this.filterCalcMenuSections(c.sections) }));
        return filteredCategories.filter((c) => c.sections.length > 0);
    }
    filterCalcMenuSections(sections) {
        const filteredSections = sections.map((s) => ({ ...s, items: this.filterMenuItems(s.items) }));
        const nonEmptySections = filteredSections.filter((s) => s.items.length > 0);
        if (!this.featureService.userHasFeatures([STAFF_ONLY_FEATURE])) {
            // Filter out sections labeled as "(Default)" for non staff users.
            // This is temporary, will be removed once default categories are made available to non staff
            return nonEmptySections.filter((c) => !c.section_label.includes('(Default)'));
        }
        return nonEmptySections;
    }
    filterMenuItems(items) {
        return items
            .map((i) => {
            // Temporary logic to remove the (Morn) calc/info suffix from menus for non staff before generic calc
            // release. This .map logic can be removed post generic calc release.
            if (!this.featureService.userHasFeatures(['staffOnly'])) {
                if ('menu_label' in i && i.menu_label && i.menu_label.endsWith(' (Morn)')) {
                    return { ...i, menu_label: i.menu_label.slice(0, -7) };
                }
                if ('label' in i && i.label && i.label.endsWith(' (Morn)')) {
                    return { ...i, label: i.label.slice(0, -7) };
                }
                if ('name' in i && i.name && i.name.endsWith(' (Morn)')) {
                    return { ...i, name: i.name.slice(0, -7) };
                }
            }
            return i;
        })
            .filter((i) => {
            // Display element if
            //     always_display is true
            //     or user has the required feature_needed
            if (i.always_display) {
                return true;
            }
            return !i.feature_needed || this.featureService.userHasFeatures([i.feature_needed]);
        });
    }
}
StaticMenuService.ɵfac = function StaticMenuService_Factory(t) { return new (t || StaticMenuService)(i0.ɵɵinject(i1.AjaxService), i0.ɵɵinject(i2.StaticFileUrlService), i0.ɵɵinject(i3.FeatureService)); };
StaticMenuService.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: StaticMenuService, factory: StaticMenuService.ɵfac, providedIn: 'root' });
